import { SpecialAttackSkill, StatusSkill } from "./skill";
import type { Skill } from "./skill";
import type { BattleStageBounds } from "./battleStageViewport";
import { SWF_STAGE_HEIGHT, SWF_STAGE_WIDTH } from "./swfBattleStage";

/**
 * SWF_CLIP_SEQUENCE_NAMES，供本模块相关规则使用。
 */
export const SWF_CLIP_SEQUENCE_NAMES = [
    "standby",
    "attack",
    "sa",
    "cp",
    "hited",
] as const;

/**
 * SwfClipSequenceName 的数据结构或可选取值约定。
 */
export type SwfClipSequenceName = (typeof SWF_CLIP_SEQUENCE_NAMES)[number];

/**
 * SwfClipFrame 的数据结构或可选取值约定。
 */
export interface SwfClipFrame {
    /** Atlas pixel rectangle. */
    x: number;
    /**
     * y，保存当前实例的y状态。
     */
    y: number;
    /**
     * width，保存当前实例的width状态。
     */
    width: number;
    /**
     * height，保存当前实例的height状态。
     */
    height: number;
    /** Top-left position of this trimmed frame in the 960x560 stage coordinates. */
    offsetX: number;
    /**
     * offsetY，保存当前实例的offsetY状态。
     */
    offsetY: number;
    /** Atlas page index for paged WebGL atlases. */
    atlas?: number;
    /** Frame duration in SWF ticks. */
    duration?: number;
    /** The source frame contains no visible pixels. */
    empty?: boolean;
}

/**
 * SwfClipAtlas 的数据结构或可选取值约定。
 */
export interface SwfClipAtlas {
    /**
     * src，保存当前实例的src状态。
     */
    src: string;
    /**
     * width，保存当前实例的width状态。
     */
    width: number;
    /**
     * height，保存当前实例的height状态。
     */
    height: number;
}

/**
 * SwfClipSequence 的数据结构或可选取值约定。
 */
export interface SwfClipSequence {
    /**
     * name，保存当前实例的name状态。
     */
    name: string;
    /**
     * frames，保存当前实例的frames状态。
     */
    frames: SwfClipFrame[];
    /**
     * loop，保存当前实例的loop状态。
     */
    loop?: boolean;
}

/**
 * SwfClipMeta 的数据结构或可选取值约定。
 */
export interface SwfClipMeta {
    /**
     * version，保存当前实例的version状态。
     */
    version: 1;
    /** True when FFDec export included pixels outside the SWF displayRect. */
    unclipped?: boolean;
    /**
     * petId，保存当前实例的petId状态。
     */
    petId: number;
    /**
     * source，保存当前实例的source状态。
     */
    source: string;
    /**
     * frameRate，保存当前实例的frameRate状态。
     */
    frameRate: number;
    /**
     * stageWidth，保存当前实例的stageWidth状态。
     */
    stageWidth: number;
    /**
     * stageHeight，保存当前实例的stageHeight状态。
     */
    stageHeight: number;
    /**
     * atlasWidth，保存当前实例的atlasWidth状态。
     */
    atlasWidth: number;
    /**
     * atlasHeight，保存当前实例的atlasHeight状态。
     */
    atlasHeight: number;
    /**
     * atlases，保存当前实例的atlases状态。
     */
    atlases?: SwfClipAtlas[];
    /**
     * sequences，保存当前实例的sequences状态。
     */
    sequences: SwfClipSequence[];
}

/**
 * LoadedSwfClip 的数据结构或可选取值约定。
 */
export interface LoadedSwfClip {
    /**
     * meta，保存当前实例的meta状态。
     */
    meta: SwfClipMeta;
    /**
     * atlas，保存当前实例的atlas状态。
     */
    atlas: string;
    /**
     * atlases，保存当前实例的atlases状态。
     */
    atlases: string[];
    /**
     * baseUrl，保存当前实例的baseUrl状态。
     */
    baseUrl: string;
}

/**
 * cache，供本模块相关规则使用。
 */
const cache = new Map<string, Promise<LoadedSwfClip>>();

/**
 * normalizeBaseUrl：执行对应的领域操作。
 * @param baseUrl 资源目录地址
 * @param petId 精灵唯一序号
 * @returns 本次操作的结果；具体取值由当前状态与分支决定。
 */
function normalizeBaseUrl(baseUrl: string, petId: string | number): string {
    /**
     * trimmed，供本模块相关规则使用。
     */
    const trimmed = baseUrl.replace(/\/$/, "");
    return trimmed || `/swfclips/${encodeURIComponent(String(petId))}`;
}

/**
 * isSequence：执行对应的领域操作。
 * @param value 待处理的值
 * @returns 本次操作的结果；具体取值由当前状态与分支决定。
 */
function isSequence(value: unknown): value is SwfClipSequence {
    if (!value || typeof value !== "object") return false;
    /**
     * sequence，供本模块相关规则使用。
     */
    const sequence = value as Partial<SwfClipSequence>;
    return (
        typeof sequence.name === "string" &&
        Array.isArray(sequence.frames) &&
        sequence.frames.every(isFrame)
    );
}

/**
 * isFrame：执行对应的领域操作。
 * @param value 待处理的值
 * @returns 本次操作的结果；具体取值由当前状态与分支决定。
 */
function isFrame(value: unknown): value is SwfClipFrame {
    if (!value || typeof value !== "object") return false;
    /**
     * frame，供本模块相关规则使用。
     */
    const frame = value as Partial<SwfClipFrame>;
    /**
     * requiredNumbers，供本模块相关规则使用。
     */
    const requiredNumbers = [
        frame.x,
        frame.y,
        frame.width,
        frame.height,
        frame.offsetX,
        frame.offsetY,
    ].every((entry) => typeof entry === "number" && Number.isFinite(entry));
    /**
     * validAtlas，供本模块相关规则使用。
     */
    const validAtlas =
        frame.atlas === undefined ||
        (Number.isSafeInteger(frame.atlas) && frame.atlas >= 0);
    /**
     * validDuration，供本模块相关规则使用。
     */
    const validDuration =
        frame.duration === undefined ||
        (typeof frame.duration === "number" &&
            Number.isFinite(frame.duration) &&
            frame.duration > 0);
    /**
     * validEmpty，供本模块相关规则使用。
     */
    const validEmpty = frame.empty === undefined || typeof frame.empty === "boolean";
    return requiredNumbers && validAtlas && validDuration && validEmpty;
}

/**
 * isAtlas：执行对应的领域操作。
 * @param value 待处理的值
 * @returns 本次操作的结果；具体取值由当前状态与分支决定。
 */
function isAtlas(value: unknown): value is SwfClipAtlas {
    if (!value || typeof value !== "object") return false;
    /**
     * atlas，供本模块相关规则使用。
     */
    const atlas = value as Partial<SwfClipAtlas>;
    return (
        typeof atlas.src === "string" &&
        typeof atlas.width === "number" &&
        typeof atlas.height === "number" &&
        atlas.width > 0 &&
        atlas.height > 0
    );
}

/**
 * validateSwfClipMeta：执行对应的领域操作。
 * @param value 待处理的值
 * @returns 本次操作的结果；具体取值由当前状态与分支决定。
 */
export function validateSwfClipMeta(value: unknown): SwfClipMeta {
    if (!value || typeof value !== "object") {
        throw new Error("SWFClip manifest 格式无效");
    }
    /**
     * meta，供本模块相关规则使用。
     */
    const meta = value as Partial<SwfClipMeta>;
    if (
        meta.version !== 1 ||
        (meta.unclipped !== undefined && typeof meta.unclipped !== "boolean") ||
        typeof meta.petId !== "number" ||
        typeof meta.source !== "string" ||
        typeof meta.frameRate !== "number" ||
        typeof meta.stageWidth !== "number" ||
        typeof meta.stageHeight !== "number" ||
        typeof meta.atlasWidth !== "number" ||
        typeof meta.atlasHeight !== "number" ||
        (meta.atlases !== undefined &&
            (!Array.isArray(meta.atlases) ||
                meta.atlases.length === 0 ||
                !meta.atlases.every(isAtlas))) ||
        !Array.isArray(meta.sequences) ||
        !meta.sequences.every(isSequence)
    ) {
        throw new Error("SWFClip manifest 字段缺失或类型无效");
    }
    /**
     * validated，供本模块相关规则使用。
     */
    const validated = meta as SwfClipMeta;
    /**
     * positiveDimensions，供本模块相关规则使用。
     */
    const positiveDimensions = [
        validated.frameRate,
        validated.stageWidth,
        validated.stageHeight,
        validated.atlasWidth,
        validated.atlasHeight,
    ].every((entry) => Number.isFinite(entry) && entry > 0);
    if (!positiveDimensions) {
        throw new Error("SWFClip manifest 尺寸或帧率无效");
    }
    if (
        validated.stageWidth !== SWF_STAGE_WIDTH ||
        validated.stageHeight !== SWF_STAGE_HEIGHT
    ) {
        throw new Error("SWFClip manifest 舞台必须是固定的 960x560（12:7）");
    }
    /**
     * names，供本模块相关规则使用。
     */
    const names = new Set<string>();
    /**
     * atlasPages，供本模块相关规则使用。
     */
    const atlasPages = validated.atlases ?? [
        { src: "atlas.png", width: validated.atlasWidth, height: validated.atlasHeight },
    ];
    for (const sequence of validated.sequences) {
        if (names.has(sequence.name)) {
            throw new Error(`SWFClip 序列重复: ${sequence.name}`);
        }
        names.add(sequence.name);
        for (const frame of sequence.frames) {
            /**
             * page，供本模块相关规则使用。
             */
            const page = frame.atlas ?? 0;
            /**
             * atlas，供本模块相关规则使用。
             */
            const atlas = atlasPages[page];
            if (!atlas) throw new Error(`SWFClip 帧引用不存在的图集页: ${sequence.name}`);
            if (
                frame.x < 0 ||
                frame.y < 0 ||
                frame.width <= 0 ||
                frame.height <= 0 ||
                frame.x + frame.width > atlas.width ||
                frame.y + frame.height > atlas.height
            ) {
                throw new Error(`SWFClip 帧超出图集范围: ${sequence.name}`);
            }
        }
    }
    return validated;
}

/**
 * findSwfClipSequence：执行对应的领域操作。
 * @param clip clip参数，参与本次操作
 * @param name 名称
 * @returns 本次操作的结果；具体取值由当前状态与分支决定。
 */
export function findSwfClipSequence(
    clip: SwfClipMeta,
    name: string,
): SwfClipSequence | undefined {
    return clip.sequences.find((sequence) => sequence.name === name);
}

/**
 * getSwfClipSequence：执行对应的领域操作。
 * @param clip clip参数，参与本次操作
 * @param name 名称
 * @returns 本次操作的结果；具体取值由当前状态与分支决定。
 */
export function getSwfClipSequence(
    clip: SwfClipMeta,
    name: string,
): SwfClipSequence {
    return (
        findSwfClipSequence(clip, name) ??
        findSwfClipSequence(clip, "standby") ??
        clip.sequences[0] ??
        { name: "standby", frames: [], loop: true }
    );
}

/**
 * shouldLoopSwfClipSequence：执行对应的领域操作。
 * @param sequence sequence参数，参与本次操作
 * @returns 本次操作的结果；具体取值由当前状态与分支决定。
 */
export function shouldLoopSwfClipSequence(sequence: SwfClipSequence): boolean {
    return sequence.name === "standby" && sequence.loop !== false;
}

/**
 * Determine the visible coordinates of a sequence in the original SWF-stage
 * coordinate system. Exported frame offsets may intentionally lie outside the
 * nominal stage when an animation extends beyond the SWF display rectangle.
 */
export function swfClipSequenceBounds(
    sequence: SwfClipSequence | undefined,
    stageWidth: number,
    stageHeight: number,
    mirrored = false,
): BattleStageBounds {
    /**
     * safeStageWidth，供本模块相关规则使用。
     */
    const safeStageWidth = Number.isFinite(stageWidth) && stageWidth > 0
        ? stageWidth
        : SWF_STAGE_WIDTH;
    /**
     * safeStageHeight，供本模块相关规则使用。
     */
    const safeStageHeight = Number.isFinite(stageHeight) && stageHeight > 0
        ? stageHeight
        : SWF_STAGE_HEIGHT;
    /**
     * minX，供本模块相关规则使用。
     */
    let minX = Number.POSITIVE_INFINITY;
    /**
     * maxX，供本模块相关规则使用。
     */
    let maxX = Number.NEGATIVE_INFINITY;
    /**
     * minY，供本模块相关规则使用。
     */
    let minY = Number.POSITIVE_INFINITY;
    /**
     * maxY，供本模块相关规则使用。
     */
    let maxY = Number.NEGATIVE_INFINITY;
    for (const frame of sequence?.frames ?? []) {
        if (frame.empty) continue;
        minX = Math.min(minX, frame.offsetX);
        maxX = Math.max(maxX, frame.offsetX + frame.width);
        minY = Math.min(minY, frame.offsetY);
        maxY = Math.max(maxY, frame.offsetY + frame.height);
    }
    if (!Number.isFinite(minX)) {
        return { minX: 0, maxX: safeStageWidth, minY: 0, maxY: safeStageHeight };
    }
    if (!mirrored) return { minX, maxX, minY, maxY };
    return {
        minX: safeStageWidth - maxX,
        maxX: safeStageWidth - minX,
        minY,
        maxY,
    };
}

/**
 * Return one stable camera box for the complete clip. It includes the
 * canonical SWF stage so standby and action sequences share the same scale and
 * registration even when an action extends far outside the stage.
 */
export function swfClipAnimationBounds(
    meta: Pick<SwfClipMeta, "sequences" | "stageWidth" | "stageHeight">,
    mirrored = false,
): BattleStageBounds {
    // Rendering always uses the shared battle-stage contract. Validation
    // rejects legacy manifests, but keeping this helper defensive prevents a
    // caller that constructs metadata in memory from changing registration.
    /**
     * stageWidth，供本模块相关规则使用。
     */
    const stageWidth = SWF_STAGE_WIDTH;
    /**
     * stageHeight，供本模块相关规则使用。
     */
    const stageHeight = SWF_STAGE_HEIGHT;
    /**
     * minX，供本模块相关规则使用。
     */
    let minX = 0;
    /**
     * maxX，供本模块相关规则使用。
     */
    let maxX = stageWidth;
    /**
     * minY，供本模块相关规则使用。
     */
    let minY = 0;
    /**
     * maxY，供本模块相关规则使用。
     */
    let maxY = stageHeight;
    for (const sequence of meta.sequences) {
        for (const frame of sequence.frames) {
            if (frame.empty) continue;
            minX = Math.min(minX, frame.offsetX);
            maxX = Math.max(maxX, frame.offsetX + frame.width);
            minY = Math.min(minY, frame.offsetY);
            maxY = Math.max(maxY, frame.offsetY + frame.height);
        }
    }
    if (!mirrored) return { minX, maxX, minY, maxY };
    return {
        minX: stageWidth - maxX,
        maxX: stageWidth - minX,
        minY,
        maxY,
    };
}

/**
 * swfClipFrameDurationSeconds：执行对应的领域操作。
 * @param frame frame参数，参与本次操作
 * @param frameRate frameRate参数，参与本次操作
 * @returns 本次操作的结果；具体取值由当前状态与分支决定。
 */
export function swfClipFrameDurationSeconds(
    frame: SwfClipFrame | undefined,
    frameRate: number,
): number {
    /**
     * safeFrameRate，供本模块相关规则使用。
     */
    const safeFrameRate = Number.isFinite(frameRate)
        ? Math.max(1, frameRate)
        : 25;
    return (frame?.duration ?? 1) / safeFrameRate;
}

/** Total exported timeline duration, including each frame's repeated SWF ticks. */
export function swfClipSequenceDurationSeconds(
    meta: SwfClipMeta,
    name: string,
): number {
    /**
     * sequence，供本模块相关规则使用。
     */
    const sequence = findSwfClipSequence(meta, name);
    return sequence?.frames.reduce(
        (total, frame) => total + swfClipFrameDurationSeconds(frame, meta.frameRate),
        0,
    ) ?? 0;
}

/** Duration of the first `frameCount` exported frames in a sequence. */
export function swfClipSequencePrefixDurationSeconds(
    meta: SwfClipMeta,
    name: string,
    frameCount: number,
): number {
    /**
     * sequence，供本模块相关规则使用。
     */
    const sequence = findSwfClipSequence(meta, name);
    /**
     * count，供本模块相关规则使用。
     */
    const count = Math.max(0, Math.floor(frameCount));
    return sequence?.frames.slice(0, count).reduce(
        (total, frame) => total + swfClipFrameDurationSeconds(frame, meta.frameRate),
        0,
    ) ?? 0;
}

/**
 * swfClipSequenceForSkill：执行对应的领域操作。
 * @param skill 本次使用的技能
 * @returns 本次操作的结果；具体取值由当前状态与分支决定。
 */
export function swfClipSequenceForSkill(skill: Skill): SwfClipSequenceName {
    if (skill instanceof SpecialAttackSkill) return "sa";
    if (skill instanceof StatusSkill) return "cp";
    return "attack";
}

/**
 * fetchJson：执行对应的领域操作。
 * @param url url参数，参与本次操作
 * @returns 本次操作的结果；具体取值由当前状态与分支决定。
 */
async function fetchJson(url: string): Promise<unknown> {
    /**
     * response，供本模块相关规则使用。
     */
    const response = await fetch(url, { mode: "cors", cache: "no-store" });
    if (!response.ok) throw new Error(`SWFClip manifest 加载失败: HTTP ${response.status}`);
    return response.json();
}

/**
 * swfClipAssetUrl：执行对应的领域操作。
 * @param baseUrl 资源目录地址
 * @param source source参数，参与本次操作
 * @param unclipped unclipped参数，参与本次操作
 * @returns 本次操作的结果；具体取值由当前状态与分支决定。
 */
function swfClipAssetUrl(baseUrl: string, source: string, unclipped?: boolean): string {
    /**
     * url，供本模块相关规则使用。
     */
    const url = `${baseUrl}/${source}`;
    if (!unclipped) return url;
    return `${url}${source.includes("?") ? "&" : "?"}swfclip=unclipped-v1`;
}

/**
 * loadSwfClip：执行对应的领域操作。
 * @param baseUrl 资源目录地址
 * @param petId 精灵唯一序号
 * @returns 本次操作的结果；具体取值由当前状态与分支决定。
 */
export async function loadSwfClip(
    baseUrl: string,
    petId: string | number,
): Promise<LoadedSwfClip> {
    /**
     * normalized，供本模块相关规则使用。
     */
    const normalized = normalizeBaseUrl(baseUrl, petId);
    /**
     * cached，供本模块相关规则使用。
     */
    const cached = cache.get(normalized);
    if (cached) return cached;

    /**
     * request，供本模块相关规则使用。
     */
    const request = (async () => {
        /**
         * metaUrl，供本模块相关规则使用。
         */
        const metaUrl = `${normalized}/meta.json`;
        /**
         * meta，供本模块相关规则使用。
         */
        const meta = validateSwfClipMeta(await fetchJson(metaUrl));
        /**
         * atlases，供本模块相关规则使用。
         */
        const atlases = meta.atlases?.map((atlas) =>
            swfClipAssetUrl(normalized, atlas.src, meta.unclipped),
        ) ?? [
            swfClipAssetUrl(normalized, "atlas.png", meta.unclipped),
        ];
        return {
            meta,
            atlas: swfClipAssetUrl(normalized, "atlas.png", meta.unclipped),
            atlases,
            baseUrl: normalized,
        };
    })();
    cache.set(normalized, request);
    try {
        return await request;
    } catch (error) {
        cache.delete(normalized);
        throw error;
    }
}

/**
 * clearSwfClipCache：执行对应的领域操作。
 */
export function clearSwfClipCache() {
    cache.clear();
}
